
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { formatPhoneNumber, validatePhoneNumber } from '@/lib/masks';
import { useAuth } from '@/context/AuthContext';
import { useNavigate } from 'react-router-dom';
import ConfirmationModal from '@/components/ConfirmationModal';

interface FormData {
  email: string;
  nome: string;
  telefone: string;
  senha: string;
}

interface FormErrors {
  email?: string;
  nome?: string;
  telefone?: string;
  senha?: string;
}

interface LeadCaptureFormProps {
  campaignSource?: string;
}

const LeadCaptureForm: React.FC<LeadCaptureFormProps> = ({ campaignSource }) => {
  const [formData, setFormData] = useState<FormData>({
    email: '',
    nome: '',
    telefone: '',
    senha: '',
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [emailDeliveryIssue, setEmailDeliveryIssue] = useState(false);
  const { signUp } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    
    if (name === 'telefone') {
      setFormData({
        ...formData,
        telefone: formatPhoneNumber(value),
      });
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
    
    // Clear error when field is edited
    if (errors[name as keyof FormErrors]) {
      setErrors({ ...errors, [name]: undefined });
    }
  };

  const validateForm = () => {
    const newErrors: FormErrors = {};
    
    // Email validation
    if (!formData.email) {
      newErrors.email = 'Email é obrigatório';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email inválido';
    }
    
    // Name validation
    if (!formData.nome) {
      newErrors.nome = 'Nome é obrigatório';
    }
    
    // Phone validation (optional)
    if (formData.telefone && !validatePhoneNumber(formData.telefone)) {
      newErrors.telefone = 'Telefone inválido';
    }
    
    // Password validation
    if (!formData.senha) {
      newErrors.senha = 'Senha é obrigatória';
    } else if (formData.senha.length < 8) {
      newErrors.senha = 'A senha deve ter pelo menos 8 caracteres';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Track form submission attempt
    if (typeof window.gtag !== 'undefined') {
      window.gtag('event', 'form_start', {
        event_category: 'engagement',
        event_label: 'mobile_landing_form',
        campaign_source: campaignSource || 'instagram'
      });
    }
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const { error, emailDeliveryError } = await signUp(
        formData.email,
        formData.senha,
        {
          nome: formData.nome,
          telefone: formData.telefone,
          profissao: 'Corretor', // Default for this landing page
        }
      );
      
      if (error) {
        if (error.message?.includes('already')) {
          toast.error('Este email já está em uso');
          setErrors(prev => ({ ...prev, email: 'Este email já está em uso' }));
          
          // Track error
          if (typeof window.gtag !== 'undefined') {
            window.gtag('event', 'signup_error', {
              event_category: 'error',
              event_label: 'email_already_exists',
              campaign_source: campaignSource || 'instagram'
            });
          }
        } else {
          toast.error('Erro ao criar conta');
          
          // Track error
          if (typeof window.gtag !== 'undefined') {
            window.gtag('event', 'signup_error', {
              event_category: 'error',
              event_label: 'general_error',
              campaign_source: campaignSource || 'instagram'
            });
          }
        }
      } else {
        // Show the confirmation modal
        setEmailDeliveryIssue(!!emailDeliveryError);
        setShowConfirmModal(true);
        
        // Track successful signup
        if (typeof window.gtag !== 'undefined') {
          window.gtag('event', 'signup_complete', {
            event_category: 'conversion',
            event_label: 'new_user',
            campaign_source: campaignSource || 'instagram'
          });
        }
      }
    } catch (error) {
      toast.error('Erro ao processar seu cadastro');
      console.error('Signup error:', error);
      
      // Track error
      if (typeof window.gtag !== 'undefined') {
        window.gtag('event', 'signup_error', {
          event_category: 'error',
          event_label: 'unexpected_error',
          campaign_source: campaignSource || 'instagram'
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseModal = () => {
    setShowConfirmModal(false);
    navigate('/login');
  };

  return (
    <>
      <form onSubmit={handleSubmit} className="space-y-4 w-full max-w-md mx-auto">
        <div>
          <Input
            name="nome"
            placeholder="Seu nome completo"
            value={formData.nome}
            onChange={handleChange}
            className={`h-12 text-base ${errors.nome ? "border-red-500" : ""}`}
            autoComplete="name"
          />
          {errors.nome && <p className="text-red-500 text-xs mt-1">{errors.nome}</p>}
        </div>
        
        <div>
          <Input
            name="email"
            type="email"
            placeholder="Seu melhor email"
            value={formData.email}
            onChange={handleChange}
            className={`h-12 text-base ${errors.email ? "border-red-500" : ""}`}
            autoComplete="email"
          />
          {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
        </div>
        
        <div>
          <Input
            name="telefone"
            placeholder="Telefone"
            value={formData.telefone}
            onChange={handleChange}
            className={`h-12 text-base ${errors.telefone ? "border-red-500" : ""}`}
            autoComplete="tel"
            type="tel"
            inputMode="tel"
          />
          {errors.telefone && <p className="text-red-500 text-xs mt-1">{errors.telefone}</p>}
        </div>
        
        <div>
          <Input
            name="senha"
            type="password"
            placeholder="Senha (mínimo 8 caracteres)"
            value={formData.senha}
            onChange={handleChange}
            className={`h-12 text-base ${errors.senha ? "border-red-500" : ""}`}
            autoComplete="new-password"
          />
          {errors.senha && <p className="text-red-500 text-xs mt-1">{errors.senha}</p>}
        </div>
        
        <Button 
          type="submit" 
          className="w-full bg-blue-dark hover:bg-blue-dark/90 text-white py-6 text-lg h-14"
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <>
              <div className="h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
              Processando...
            </>
          ) : (
            "Criar Minha Conta Grátis"
          )}
        </Button>
      </form>

      {/* Confirmation Modal */}
      <ConfirmationModal 
        isOpen={showConfirmModal} 
        onClose={handleCloseModal} 
        email={formData.email}
        emailDeliveryIssue={emailDeliveryIssue}
      />
    </>
  );
};

export default LeadCaptureForm;
